import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Divider,
  Drawer,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { getStageDefinition, getStageUxConfig } from "./funnelStages";
import { CAMPAIGN_CHANNEL_KEYS, CHANNEL_LABELS } from "../campaignUi";
import { BELOW_CHROME_DRAWER_PROPS, belowChromeDrawerPaperSx } from "../belowChromeDrawer";

export default function StageConfigPanel({ open, stageId, flowConfig, onClose, onChange, onSave }) {
  const cfg = useMemo(() => getStageUxConfig(flowConfig, stageId), [flowConfig, stageId]);
  const stage = useMemo(() => getStageDefinition(stageId), [stageId]);
  const [localError, setLocalError] = useState("");

  const enabled = open && !!stageId;
  const title = stage?.label || "Etapa";
  const description = stage?.description || "";
  const example = stage?.example || "";

  const channel = cfg.channel || "";
  const integration = cfg.integration || "";
  const offer = cfg.offer || "";
  const notes = cfg.notes || "";
  const mode = cfg.mode || "simple";

  return (
    <Drawer
      anchor="right"
      open={!!enabled}
      onClose={onClose}
      ModalProps={BELOW_CHROME_DRAWER_PROPS.ModalProps}
      PaperProps={{
        sx: belowChromeDrawerPaperSx({
          width: { xs: "100%", sm: 460 },
        }),
      }}
    >
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: "auto",
          px: { xs: 2.5, sm: 3.5 },
          pt: { xs: 2.5, sm: 3 },
          pb: 2,
        }}
      >
        <Stack spacing={1}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {title}
          </Typography>
          {description ? (
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
              {description}
            </Typography>
          ) : null}
          {example ? (
            <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.5 }}>
              {example}
            </Typography>
          ) : null}
        </Stack>

        <Divider sx={{ my: 2.5 }} />

        {localError ? (
          <Alert severity="error" sx={{ mb: 2.5 }}>
            {localError}
          </Alert>
        ) : null}

        <Alert severity="info" sx={{ mb: 2.5 }}>
          Preencha o que deve acontecer nesta etapa. Isso ajuda sua equipe a manter a campanha
          organizada. O envio automático continua sendo definido no fluxo da campanha.
        </Alert>

        <Stack spacing={2.25}>
          <FormControl fullWidth>
            <InputLabel>Nível de detalhe</InputLabel>
            <Select
              label="Nível de detalhe"
              value={mode}
              onChange={(e) => {
                setLocalError("");
                onChange?.({ mode: String(e.target.value) });
              }}
            >
              <MenuItem value="simple">Simples — só o essencial</MenuItem>
              <MenuItem value="advanced">Detalhado — com mais anotações</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth>
            <InputLabel>Canal desta etapa</InputLabel>
            <Select
              label="Canal desta etapa"
              value={channel}
              onChange={(e) => onChange?.({ channel: String(e.target.value) })}
            >
              <MenuItem value="">Usar o mesmo canal da campanha</MenuItem>
              {CAMPAIGN_CHANNEL_KEYS.map((key) => (
                <MenuItem key={key} value={key}>
                  {CHANNEL_LABELS[key]}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="Ferramenta / integração (opcional)"
            value={integration}
            onChange={(e) => onChange?.({ integration: e.target.value })}
            placeholder="Ex.: Meta Ads, RD Station"
            helperText="Se esta etapa depende de alguma ferramenta externa"
            fullWidth
          />

          {stageId === "conversion" ? (
            <TextField
              label="Oferta desta etapa"
              value={offer}
              onChange={(e) => onChange?.({ offer: e.target.value })}
              placeholder="Ex.: Plano Pro com 7 dias grátis"
              fullWidth
            />
          ) : null}

          <TextField
            label="O que deve acontecer aqui"
            value={notes}
            onChange={(e) => onChange?.({ notes: e.target.value })}
            fullWidth
            multiline
            minRows={4}
            placeholder="Descreva a mensagem, o critério de sucesso ou o próximo passo."
            helperText="Ex.: enviar DM de boas-vindas e perguntar o interesse da pessoa"
          />
        </Stack>
      </Box>

      <Box
        sx={{
          flexShrink: 0,
          px: { xs: 2.5, sm: 3.5 },
          py: 2,
          borderTop: "1px solid",
          borderColor: "divider",
          bgcolor: "background.paper",
        }}
      >
        <Stack direction="row" spacing={1.25} justifyContent="flex-end" flexWrap="wrap">
          <Button variant="outlined" onClick={onClose}>
            Fechar
          </Button>
          <Button
            variant="contained"
            onClick={async () => {
              try {
                setLocalError("");
                await onSave?.();
                onClose?.();
              } catch {
                setLocalError("Não foi possível salvar agora. Tente novamente.");
              }
            }}
          >
            Salvar etapa
          </Button>
        </Stack>
      </Box>
    </Drawer>
  );
}
